import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@jhool-io/fe-components";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useGetProviderNoteAuditReport } from "../../hooks/queries/reports.queries";
import { INoteAuditReportRow } from "../../types/reports.types";
import {
    convertTimeInSecondsToOtherTimeUnits,
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import { SessionLabels } from "../../../../utils/types/session";
import { NoteLabels } from "../../../notes/types/notes.types";

import { getNoteStatusTag } from "../../../notes/helpers/notes.helpers";
import { useDebounce } from "../../../../hooks/helpers";

interface NoteAuditReportProviderBreakdownProps {
    row: INoteAuditReportRow;
    isExpanded: boolean;
}

export default function NoteAuditReportProviderBreakdown({
    row,
    isExpanded,
}: NoteAuditReportProviderBreakdownProps) {
    const [searchParams] = useSearchParams();

    const providerNameFilter = searchParams.get("provider_name") || "";
    const providerCredentialFilter =
        searchParams.get("provider_credential") || "";
    const customDateFilter = searchParams.get("custom_date") || "this_month";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const fromDateCreatedAtFilter = searchParams.get("from_created_at_date");
    const toDateCreatedAtFilter = searchParams.get("to_created_at_date");
    const includeNonBillableNotesFilter =
        searchParams.get("include_non_billable_notes") || "";

    const name = useDebounce(providerNameFilter, 500);

    const { data, isLoading, error, isSuccess } = useGetProviderNoteAuditReport(
        row.provider_id,
        {
            provider_name: name,
            provider_credential: providerCredentialFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            custom_date: customDateFilter,
            from_created_at_date: fromDateCreatedAtFilter,
            to_created_at_date: toDateCreatedAtFilter,
            include_non_billable_notes: includeNonBillableNotesFilter,
        },
        isExpanded
    );

    const filteredData = isSuccess
        ? data.data.activity_list.filter(
              (item) =>
                  item.is_note_late ||
                  Number(item.changes_requested_count || 0) > 0
          )
        : [];

    return (
        <div>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display provider note audit list at this time `
                    }
                    context="general"
                />
            )}
            {isSuccess && filteredData.length === 0 && (
                <ListState
                    stateHelperText="Provider note audit listt will appear here once available"
                    isError={false}
                    emptyMessage="No provider note audit activity list to display"
                    context="general"
                />
            )}
            {isSuccess && filteredData.length > 0 ? (
                <div className="overflow-x-auto max-w-full">
                    <div className="grid grid-cols-11 gap-[--spacing-items] text-caption py-[--padding-card-y] px-[--padding-card-x] text-textSec [border-bottom:1px_solid_var(--color-stroke)] uppercase gap-x-2">
                        <div>CLIENT</div>
                        <div>DATE OF SERVICE</div>
                        <div>Changes requested</div>
                        <div>SERVICE TYPE</div>
                        <div>SESSION TIME</div>
                        <div>DURATION</div>
                        <div>NOTE TYPE</div>
                        <div>DATE CREATED</div>
                        <div>LATE NOTE</div>
                        <div className="col-[10_/_span_11]">STATUS</div>
                    </div>
                    {data.data.activity_list
                        .filter(
                            (item) =>
                                item.is_note_late ||
                                Number(item.changes_requested_count || 0) > 0
                        )
                        .map((item) => (
                            <Link
                                to={`/notes/${item.client_id}/${item.note_id}`}
                                key={item.note_id}
                                className="grid grid-cols-11 gap-[--spacing-items] text-caption py-[--padding-card-y] px-[--padding-card-x] text-text font-medium [border-bottom:1px_solid_var(--color-stroke)] hover:bg-interactionHover hover:cursor-pointer gap-x-2"
                            >
                                <span>
                                    {displayNameInRightFormat({
                                        firstName: item.client.first_name,
                                        lastName: item.client.last_name,
                                    })}
                                </span>
                                <span>
                                    {formatDate(item.date_of_service) || "--"}
                                </span>
                                <span>{item.changes_requested_count || 0}</span>
                                <span>
                                    {SessionLabels[item.service] || "--"}
                                </span>
                                <span>
                                    {formatDate(
                                        item.start_date_time,
                                        false,
                                        "HH:mmaa"
                                    )}
                                </span>
                                <span>
                                    {convertTimeInSecondsToOtherTimeUnits(
                                        Number(item.duration_secs)
                                    )}
                                </span>
                                <div>{NoteLabels[item.note_type] || "--"}</div>
                                <div>{formatDate(item.created_at)}</div>
                                <div>
                                    {item.is_note_late ? (
                                        <Badge content="Yes" type="success" />
                                    ) : (
                                        <Badge content="No" type="danger" />
                                    )}
                                </div>
                                <div className="col-[10_/_span_11]">
                                    {!item.note_status ? (
                                        <Badge
                                            content="Cancelled, No Note"
                                            type="danger"
                                        />
                                    ) : (
                                        getNoteStatusTag(item.note_status)
                                    )}
                                </div>
                            </Link>
                        ))}
                </div>
            ) : null}
        </div>
    );
}
